'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FullscreenImageViewer } from '@/components/ui/fullscreen-viewer';

type FullscreenAsset = {
  src: string;
  alt?: string;
  title?: string;
  type?: 'image' | 'video' | '3d';
};

type OpenOptions = {
  src: string;
  alt?: string;
  type?: 'image' | 'video' | '3d';
  assets?: Array<FullscreenAsset>;
  initialIndex?: number;
};

type FullscreenViewerContextValue = {
  isOpen: boolean;
  open: (options: OpenOptions) => void;
  close: () => void;
};

const FullscreenViewerContext = createContext<FullscreenViewerContextValue | null>(null);

export function FullscreenViewerProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [assets, setAssets] = useState<Array<FullscreenAsset>>([]);
  const [initialIndex, setInitialIndex] = useState<number>(0);
  const [src, setSrc] = useState<string>('');
  const [alt, setAlt] = useState<string>('');
  const [type, setType] = useState<'image' | 'video' | '3d'>('image');

  const open = useCallback((options: OpenOptions) => {
    const { src, alt, type, assets: providedAssets, initialIndex } = options;
    const normalizedAssets: Array<FullscreenAsset> = Array.isArray(providedAssets)
      ? providedAssets
      : [{ src, alt, type }];

    const resolvedIndex = typeof initialIndex === 'number'
      ? initialIndex
      : Math.max(0, normalizedAssets.findIndex((a) => a.src === src));

    setAssets(normalizedAssets);
    setInitialIndex(resolvedIndex);
    setSrc(src);
    setAlt(alt ?? 'Asset');
    setType(type ?? 'image');
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<FullscreenViewerContextValue>(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return (
    <FullscreenViewerContext.Provider value={value}>
      {children}
      <FullscreenImageViewer
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        src={src}
        alt={alt}
        type={type}
        assets={assets.length > 0 ? assets : undefined}
        initialIndex={initialIndex}
      />
    </FullscreenViewerContext.Provider>
  );
}

export function useFullscreenViewer(): FullscreenViewerContextValue {
  const ctx = useContext(FullscreenViewerContext);
  if (!ctx) {
    throw new Error('useFullscreenViewer must be used within a FullscreenViewerProvider');
  }
  return ctx;
}
